import { MapBounds, DayForecast, DiscoverPOI } from '../types';
import { getCache, setCache, CACHE_TTL } from './cacheService';
import { directOverpassFetchWithSignal } from './overpassService';
import { snapBboxToGrid } from '../utils/boundsUtils';

/**
 * Normalize a raw POI image reference into a directly-loadable <img> src.
 *
 * OSM `image` tags (and some heritage URLs) are notoriously messy and were the
 * cause of broken thumbnails in Discover:
 *   - `http://…`            → blocked as mixed content on the HTTPS app
 *   - `File:Foo.jpg` or a
 *     commons.wikimedia.org/wiki/File:… page URL → an HTML page, not an image
 *   - anything non-http(s)  → unusable
 *
 * Wikimedia Commons references are rewritten to `Special:FilePath`, which 302s
 * straight to the actual image at a sane width and is hotlink/CORS friendly.
 * Returns undefined when the reference can't be turned into a usable image URL,
 * so the card falls back to its category icon instead of a broken-image box.
 */
export const normalizeDiscoverImageUrl = (raw?: string): string | undefined => {
  if (!raw || typeof raw !== 'string') return undefined;
  let url = raw.trim();
  if (!url) return undefined;

  // Wikimedia Commons: bare "File:Xxx" / "Fil:Xxx" / "Image:Xxx" …
  let m = url.match(/^(?:File|Fil|Image):(.+)$/i);
  // … or a full commons page URL.
  if (!m) m = url.match(/commons\.wikimedia\.org\/wiki\/(?:File|Fil|Image):(.+)$/i);
  if (m) {
    let file = m[1];
    try { file = decodeURIComponent(file); } catch { /* keep raw */ }
    return `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(file)}?width=400`;
  }

  // Upgrade http → https to avoid mixed-content blocking on the HTTPS app.
  if (/^http:\/\//i.test(url)) url = 'https://' + url.slice('http://'.length);

  // Only accept absolute https URLs; reject anything else (protocol-relative,
  // bare filenames, ftp, data:, etc.) so we don't hand the <img> a dud.
  return /^https:\/\//i.test(url) ? url : undefined;
};

// --- Weather (MET Norway Locationforecast 2.0) ---

const MET_USER_AGENT = 'Fricamp/1.0 post@takleconsulting.com';

export const fetchWeatherForecast = async (
  lat: number,
  lon: number,
  signal?: AbortSignal,
): Promise<DayForecast[]> => {
  // Truncate to 4 decimals (MET requirement)
  const rlat = Math.round(lat * 10000) / 10000;
  const rlon = Math.round(lon * 10000) / 10000;
  const cacheKey = `discover_weather_${rlat}_${rlon}`;

  const cached = getCache(cacheKey);
  if (cached) return cached as DayForecast[];

  const url = `https://api.met.no/weatherapi/locationforecast/2.0/compact?lat=${rlat}&lon=${rlon}`;
  const res = await fetch(url, {
    headers: { 'User-Agent': MET_USER_AGENT },
    signal,
  });
  if (!res.ok) throw new Error(`MET API ${res.status}`);

  const json = await res.json();
  const timeseries: any[] = json?.properties?.timeseries ?? [];

  // Group by date (local) and compute daily min/max + pick representative symbol
  const dayMap = new Map<string, { temps: number[]; symbol: string }>();
  const now = new Date();

  for (const ts of timeseries) {
    const d = new Date(ts.time);
    // Only look at next 5 days
    const diffDays = (d.getTime() - now.getTime()) / 86400000;
    if (diffDays > 5.5) break;

    const dateStr = d.toISOString().slice(0, 10);
    if (!dayMap.has(dateStr)) dayMap.set(dateStr, { temps: [], symbol: '' });
    const entry = dayMap.get(dateStr)!;

    const temp = ts.data?.instant?.details?.air_temperature;
    if (typeof temp === 'number') entry.temps.push(temp);

    // Pick the midday (12:00) symbol or first available next_6_hours symbol
    const hour = d.getUTCHours();
    const sym6 = ts.data?.next_6_hours?.summary?.symbol_code;
    const sym1 = ts.data?.next_1_hours?.summary?.symbol_code;
    if ((hour >= 10 && hour <= 14 && (sym6 || sym1)) || !entry.symbol) {
      entry.symbol = sym1 || sym6 || entry.symbol;
    }
  }

  const forecasts: DayForecast[] = [];
  for (const [date, { temps, symbol }] of dayMap) {
    if (temps.length === 0) continue;
    forecasts.push({
      date,
      symbolCode: symbol || 'cloudy',
      tempMin: Math.round(Math.min(...temps)),
      tempMax: Math.round(Math.max(...temps)),
    });
    if (forecasts.length >= 5) break;
  }

  setCache(cacheKey, forecasts, CACHE_TTL.LONG);
  return forecasts;
};

// --- Tourism POIs (Overpass / OpenStreetMap) ---

const TOURISM_OVERPASS_QUERY = (b: MapBounds) => `
[out:json][timeout:15][maxsize:2097152];
(
  node["tourism"="viewpoint"](${b.latMin},${b.lonMin},${b.latMax},${b.lonMax});
  node["tourism"="attraction"](${b.latMin},${b.lonMin},${b.latMax},${b.lonMax});
  node["tourism"="museum"](${b.latMin},${b.lonMin},${b.latMax},${b.lonMax});
  node["natural"="waterfall"](${b.latMin},${b.lonMin},${b.latMax},${b.lonMax});
  node["historic"="monument"](${b.latMin},${b.lonMin},${b.latMax},${b.lonMax});
  node["historic"="memorial"](${b.latMin},${b.lonMin},${b.latMax},${b.lonMax});
  node["historic"="castle"](${b.latMin},${b.lonMin},${b.latMax},${b.lonMax});
  node["historic"="ruins"](${b.latMin},${b.lonMin},${b.latMax},${b.lonMax});
  node["natural"="beach"](${b.latMin},${b.lonMin},${b.latMax},${b.lonMax});
  node["shop"="mall"](${b.latMin},${b.lonMin},${b.latMax},${b.lonMax});
  way["tourism"="attraction"](${b.latMin},${b.lonMin},${b.latMax},${b.lonMax});
  way["tourism"="museum"](${b.latMin},${b.lonMin},${b.latMax},${b.lonMax});
  way["historic"="castle"](${b.latMin},${b.lonMin},${b.latMax},${b.lonMax});
  way["natural"="beach"](${b.latMin},${b.lonMin},${b.latMax},${b.lonMax});
  way["shop"="mall"](${b.latMin},${b.lonMin},${b.latMax},${b.lonMax});
);
out center 20;
`;

/** OSM-tags → Oppdag-kategori. Eksportert for test. */
export const osmCategoryFromTags = (tags: Record<string, string>): string => {
  if (tags.tourism === 'viewpoint') return 'viewpoint';
  if (tags.tourism === 'museum') return 'museum';
  if (tags.tourism === 'attraction') return 'attraction';
  if (tags.natural === 'waterfall') return 'waterfall';
  if (tags.natural === 'beach') return 'beach';
  if (tags.shop === 'mall') return 'shopping';
  if (tags.historic === 'monument') return 'monument';
  if (tags.historic === 'memorial') return 'memorial';
  if (tags.historic === 'castle') return 'castle';
  if (tags.historic === 'ruins') return 'ruins';
  return 'attraction';
};

export const fetchTourismPOIs = async (
  bounds: MapBounds,
  signal?: AbortSignal,
): Promise<DiscoverPOI[]> => {
  const bboxKey = snapBboxToGrid(bounds, 0.05);
  const cacheKey = `discover_tourism_${bboxKey}`;

  const cached = getCache(cacheKey);
  if (cached) return cached as DiscoverPOI[];

  const query = TOURISM_OVERPASS_QUERY(bounds);
  const data = await directOverpassFetchWithSignal(query, signal);
  const elements: any[] = data?.elements ?? [];

  const pois: DiscoverPOI[] = elements
    .filter((el: any) => {
      const lat = el.lat ?? el.center?.lat;
      return lat != null;
    })
    .map((el: any) => {
      const lat = el.lat ?? el.center?.lat;
      const lon = el.lon ?? el.center?.lon;
      const tags = el.tags || {};
      return {
        id: `osm_${el.id}`,
        name: tags.name || tags['name:no'] || tags['name:en'] || 'Ukjent',
        type: 'attraction' as const,
        category: osmCategoryFromTags(tags),
        lat,
        lon,
        description: tags.description || tags['description:no'] || undefined,
        imageUrl: normalizeDiscoverImageUrl(tags.image),
        url: tags.website || tags.url || (typeof tags.wikipedia === 'string'
          ? `https://${tags.wikipedia.includes(':') ? tags.wikipedia.split(':')[0] : 'no'}.wikipedia.org/wiki/${encodeURIComponent(tags.wikipedia.includes(':') ? tags.wikipedia.split(':').slice(1).join(':') : tags.wikipedia)}`
          : undefined),
      };
    })
    .slice(0, 20);

  setCache(cacheKey, pois, CACHE_TTL.DEFAULT);
  return pois;
};

// --- Cultural Heritage (Riksantikvaren Brukerminner API) ---

export const fetchCulturalHeritage = async (
  bounds: MapBounds,
  signal?: AbortSignal,
): Promise<DiscoverPOI[]> => {
  const bboxKey = snapBboxToGrid(bounds, 0.05);
  const cacheKey = `discover_heritage_${bboxKey}`;

  const cached = getCache(cacheKey);
  if (cached) return cached as DiscoverPOI[];

  const bbox = `${bounds.lonMin},${bounds.latMin},${bounds.lonMax},${bounds.latMax}`;
  const url = `https://api.ra.no/brukerminner/collections/brukerminner/items?bbox=${bbox}&limit=20&f=json`;

  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`Kulturminnesøk API ${res.status}`);

  const json = await res.json();
  const features: any[] = json?.features ?? [];

  const pois: DiscoverPOI[] = features
    .filter((f: any) => f.geometry?.coordinates)
    .map((f: any) => {
      const [lon, lat] = f.geometry.coordinates;
      const props = f.properties || {};
      const img = props.bilder?.[0];
      return {
        id: `ra_${f.id}`,
        name: props.tittel || 'Ukjent kulturminne',
        type: 'heritage' as const,
        category: 'cultural_heritage',
        lat,
        lon,
        description: props.beskrivelse || undefined,
        municipality: props.kommune || undefined,
        imageUrl: normalizeDiscoverImageUrl(img?.url),
        photographer: img?.fotograf || undefined,
        url: props.eksternLenke || undefined,
      };
    });

  setCache(cacheKey, pois, CACHE_TTL.DEFAULT);
  return pois;
};
